import {
  Box,
  Button,
  Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import _ from "lodash";
import RightArrow from "../../components/icons/RightArrow";
import { useDispatch, useSelector } from "react-redux";
import {
  bloqcibeApi,
  useGetSubjectDetailQuery,
  useGetTrialDetailsQuery,
  useReEnrollSubjectMutation
} from "../../store/slices/apiSlice";
import { getUserRole } from "../util";
import SubjectDetailCRFInfo from "./SubjectDetailCRFInfo";
import CustomButton from "../../components/@extended/CustomButton";
import VisitList from "./visitDetailCRF/VisitList";
import { openMessage } from "../../store/slices/showMessageSlice";
import AuditExportButton from "../../components/common/AuditExportButton";
import QueryListDialog from "../trialSiteDetails/QueryListDialog";
import ConfirmationDialog from "../../components/common/ConfirmationDialog";
import SubjectParticipantIdHeading from "../../components/common/SubjectParticipantIdHeading";
import { SUBJECT_STATUSES } from "../../util/constants";


const SubjectDetails = ({ }) => {
  const navigate = useNavigate();
  const { trialId, siteId, trialSiteId, subjectMasterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const {user: loggedInUser, userData} = useSelector((state) => state.auth);
  const [view, setView] = useState(false);
  // const [userRole, setUserRole] = useState(null)
  const dispatch = useDispatch()
  const crfDocumentKey = useSelector((state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.crfForm);
  const enrolmentDocumentKey = useSelector((state) => state?.trial?.trialSiteDetail?.siteTrialData?.trial?.eConsentForm);
  const [crfFormData, setCrfFormData] = useState(null)
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const [subjectEnrollmentFormData, setSubjectEnrollmentFormData] = useState(null);
  const { data: subjectDetailWithSteps } = useGetSubjectDetailQuery(subjectMasterId);
  const [getLibraryForm] = bloqcibeApi.endpoints.getLibraryForm.useLazyQuery();
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [reEnrollDialogOpen, setReEnrollDialogOpen] = useState(false);
  const theme = useTheme();
  //START USING V2 API
  const { data: trialDetails } = useGetTrialDetailsQuery({
    id: trialId,
    sponsorId: sponsorId,
  });
  const [reEnrollSubject, { isLoading }] = useReEnrollSubjectMutation();

  const queryParams = useMemo(() => {
    const queryStepKey = searchParams.get('stepKey');
    const querySectionKey = searchParams.get('sectionKey');
    const querySubSectionKey = searchParams.get('subSectionKey');
    const queryFieldKey = searchParams.get('fieldKey');
    return {
      stepKey: queryStepKey,
      sectionKey: querySectionKey,
      subSectionKey: querySubSectionKey,
      fieldKey: queryFieldKey,
    };
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      if (crfDocumentKey) {
        const response = await getLibraryForm(
          `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/bloqcube/${crfDocumentKey}`
        );
        if (response.data) {
          setCrfFormData(response.data);
        }
      }
    })();
  }, [crfDocumentKey]);


  useEffect(() => {
    (async () => {
      if (enrolmentDocumentKey) {
        const response = await getLibraryForm(
          `${process.env.REACT_APP_API_ENDPOINT_URL}form-library/library/bloqcube/${enrolmentDocumentKey}`
        //  `http://localhost:3003/form-library/library/bloqcube/${enrolmentDocumentKey}`
        );
        if (response.data) {
          setSubjectEnrollmentFormData(response.data);
        }
      }
    })();
  }, [enrolmentDocumentKey]);

  const crfStepStatus = React.useMemo(() => {
    if (subjectDetailWithSteps) {

      const steps = _.sortBy(subjectDetailWithSteps?.crfDetail?.stepStatus, 'order');
      return steps;
    }
    return [];
  }, [subjectDetailWithSteps]);

  const enrollmentStepStatus = React.useMemo(() => {
    if (subjectDetailWithSteps) {
      const steps = _.sortBy(subjectDetailWithSteps?.stepStatus, 'order');
      return steps;
    }
    return [];
  }, [subjectDetailWithSteps]);

  const openStepForm = (stepKey) => {
    navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/${stepKey}/${subjectMasterId}`)
  };

  const openCRFForm = (crfMasterId, stepKey) => {
    navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/${stepKey}/${subjectMasterId}/crf/${crfMasterId}`)
  };


  const userRole = useMemo(() => {
    return getUserRole(loggedInUser, trialId, siteId, userData);
  }, [loggedInUser, userData]);

  /**
   * Opens the re-enrollment confirmation dialog
   */
  const openReEnrollDialog = useCallback(() => {
    setReEnrollDialogOpen(true);
  }, []);

  /**
   * Closes the re-enrollment confirmation dialog
   */
  const closeReEnrollDialog = useCallback(() => {
    setReEnrollDialogOpen(false);
  }, []);

  /**
   * Handle re-enrollment of a subject from ON_HOLD status
   * This function is called when the user confirms the re-enrollment in the dialog
   */
  const handleReEnrollConfirm = useCallback(async () => {
    try {
      const result = await reEnrollSubject({
        sponsorId,
        trialId,
        siteId,
        subjectMasterId: Number(subjectMasterId),
        reason: 'Re-enrollment approved',
      }).unwrap();
      
      setReEnrollDialogOpen(false);
      
      dispatch(
        openMessage({
          message: result?.message || 'Subject successfully re-enrolled to study.',
          messageSeverity: 'success',
        })
      );
    } catch (error) {
      console.error('Re-enrollment failed:', error);
      setReEnrollDialogOpen(false);
      
      dispatch(
        openMessage({
          message: error?.data?.message || error?.message || 'Failed to re-enroll subject',
          messageSeverity: 'error',
        })
      );
    }
  }, [reEnrollSubject, sponsorId, trialId, siteId, subjectMasterId, dispatch]);

  const buttonProperty = useMemo(() => {
    if (!subjectDetailWithSteps && !subjectEnrollmentFormData && !userRole) {
      return null;
    }

    // Check if subject is ON_HOLD - show Re-Enroll button
    if (subjectDetailWithSteps?.status === SUBJECT_STATUSES.ON_HOLD) {
      // Only PI and site_coordinator can re-enroll subjects
      const canReEnroll = userRole === 'site_monitor';

      if (canReEnroll) {
        return {
          buttonLabel: 'Re-Enroll Subject',
          actionClick: openReEnrollDialog,
          disabled: isLoading,
          actionButtons: [],
          buttonLableAbrivation: 'Re-enroll subject from holding area',
          isOnHold: true,
        };
      } else {
        // For other roles, just show that subject is on hold without action
        return {
          buttonLabel: '',
          actionClick: null,
          disabled: true,
          actionButtons: [],
          buttonLableAbrivation: '',
          isOnHold: true,
        };
      }
    }

    const steps = enrollmentStepStatus;
    let buttonLabel = '';
    let actionClick = null;
    let disabled = false;
    let buttonLableAbrivation = '';
    let actionButtons = [];
    let enrollStepFound = false;
    for (let i = 0; i < steps?.length; i++) {
      const stepStatusData = steps[i];
      const stepFormConfig = _.find(subjectEnrollmentFormData?.steps, (step) => step.key === stepStatusData.stepKey);
      const _actionsConfig = stepFormConfig?.access?.actions;
      const userAccess = JSON.parse(stepStatusData.userAccess);
      const actions = userAccess?.actions;
      if (stepStatusData.status === 'Pending') {
        enrollStepFound = true;
        const editAccess = userAccess?.edit;
        const isAccess =
          typeof editAccess !== 'string'
            ? _.find(editAccess, (access) => {
                return access == userRole;
              })
            : editAccess === userRole;
        if (isAccess) {
          buttonLabel = `Start ${stepStatusData.stepLabel}`;
          actionClick = () => {
            openStepForm(stepStatusData.stepKey);
          };
          buttonLableAbrivation = stepFormConfig?.lableAbrivation ? `Start ${stepFormConfig?.lableAbrivation}` : '';
        }
        break;
      } else if (stepStatusData.status === 'Verification_Pending') {
        const verifyAccess = userAccess?.verify;
        const isAccess = _.find(verifyAccess, (access) => {
          return access == userRole;
        });
        if (isAccess) {
          enrollStepFound = true;
          console.log(stepStatusData.stepKey, enrollStepFound);
        }
        if (isAccess && !buttonLabel && !actionClick && !actions) {
          buttonLabel = `Verify ${stepStatusData.stepLabel}`;
          actionClick = () => {
            openStepForm(stepStatusData.stepKey);
          };
          buttonLableAbrivation = stepFormConfig?.lableAbrivation ? `Verify ${stepFormConfig?.lableAbrivation}` : '';
        }
        const buttons = [];
        if (actions && _actionsConfig && _actionsConfig[userRole] && _actionsConfig[userRole][stepStatusData.status]) {
          buttons.push({
            buttonLabel: _actionsConfig[userRole][stepStatusData.status]['label'],
            actionClick: () => {
              openStepForm(stepStatusData.stepKey);
            },
            lableAbrivation: stepFormConfig.lableAbrivation ? stepFormConfig.lableAbrivation : '',
          });
        }
        actionButtons = [...actionButtons, ...buttons];
        if (isAccess) break;
      } else if (['Completed', 'External_Verification_Pending', 'Withdrawal', 'Enrollment_Failure'].includes(stepStatusData.status)) {
        // i === steps.length - 1
        //   ? (enrollStepFound = false)
        //   : (enrollStepFound = true);
        const buttons = [];
        if (actions && _actionsConfig && _actionsConfig[userRole] && _actionsConfig[userRole][stepStatusData.status]) {
          buttons.push({
            buttonLabel: _actionsConfig[userRole][stepStatusData.status]['label'],
            actionClick: () => {
              openStepForm(stepStatusData.stepKey);
            },
            lableAbrivation: stepFormConfig?.lableAbrivation,
          });
        }
        actionButtons = [...actionButtons, ...buttons];
      }
    }
    //if no step find
    if (!enrollStepFound) {
      for (let i = 0; i < crfStepStatus?.length; i++) {
        const stepStatusData = crfStepStatus[i];
        const userAccess = JSON.parse(stepStatusData.userAccess);
        // const actions = userAccess.actions;
        const stepFormConfig = _.find(crfStepStatus?.steps, (step) => step.key === stepStatusData.stepKey);
        if (stepStatusData.status === 'Pending') {
          const editAccess = userAccess?.edit;
          const isAccess =
            typeof editAccess !== 'string'
              ? _.find(editAccess, (access) => {
                  return access == userRole;
                })
              : editAccess === userRole;
          if (isAccess) {
            buttonLabel = `Start ${stepStatusData.stepLabel}`;
            actionClick = () => {
              openCRFForm(subjectDetailWithSteps?.crfDetail?.id, stepStatusData.stepKey);
            };
            buttonLableAbrivation = stepFormConfig?.lableAbrivation ? `Start ${stepFormConfig?.lableAbrivation}` : '';
          }
          break;
        } else if (stepStatusData.status == 'Verification_Pending') {
          const verifyAccess = userAccess?.verify;
          const isAccess = _.find(verifyAccess, (access) => {
            return access == userRole;
          });
          if (isAccess) {
            buttonLabel = `Verify ${stepStatusData.stepLabel}`;
            actionClick = () => {
              openCRFForm(subjectDetailWithSteps?.crfDetail?.id, stepStatusData.stepKey);
            };
            buttonLableAbrivation = stepFormConfig?.lableAbrivation ? `Verify ${stepFormConfig?.lableAbrivation}` : '';
            break;
          }
        }
      }
    }
    return {
      buttonLabel,
      actionClick,
      disabled,
      actionButtons,
      buttonLableAbrivation,
    };
  }, [
    enrollmentStepStatus,
    crfStepStatus,
    subjectEnrollmentFormData,
    userRole,
    subjectDetailWithSteps,
    isLoading,
    openReEnrollDialog,
  ]);

  const tableData = useMemo(() => {
    if (crfStepStatus && crfFormData) {
      let columns = [
        {
          key: 'col1',
          label: 'Section',
          minWidth: 80,
          align: 'left',
        },
      ];
      let rows = [];
      if (view) {
        for (let i = 0; i < crfStepStatus.length; i++) {
          const stepData = _.find(crfFormData.steps, (step) => step.key === crfStepStatus[i].stepKey);
          crfStepStatus[i].sectionStatuses.forEach((section) => {
            let rowIndex = rows.findIndex((row) => row.key === section.sectionKey);
            const sectionData = _.find(stepData?.sections, (_section) => _section.key === section.sectionKey);
            let obj = {
              key: section.sectionKey,
              col1: section.sectionLabel,
              stepKey: crfStepStatus[i].stepKey,
              stepStatus: crfStepStatus[i].status,
              sectionLabel: section.sectionLabel,
              crfMasterId: crfStepStatus[i].crfMasterId,
              lableAbrivation: sectionData?.lableAbrivation,
            };
            let status;
            if (section.status === 'Pending' && section.subSectionStatuses.length !== 0) {
              let incomplete = section?.subSectionStatuses.filter((subSection) => subSection.status === 'Pending');
              status = `${section?.subSectionStatuses.length - incomplete.length}/${
                section?.subSectionStatuses.length
              }`;
            } else {
              status = section?.status;
            }

            if (rowIndex !== -1) {
              rows[rowIndex][crfStepStatus[i].stepKey] = status;
            } else {
              obj[crfStepStatus[i].stepKey] = status;
              rows.push(obj);
            }
          });
          columns.push({
            key: crfStepStatus[i].stepKey,
            label: crfStepStatus[i].stepLabel,
            minWidth: 100,
            align: 'center',
          });
        }
        return { columns, rows };
      } else {
        for (let i = 0; i < crfStepStatus.length; i++) {
          const stepData = _.find(crfFormData.steps, (step) => step.key === crfStepStatus[i].stepKey);
          let row = {
            stepKey: crfStepStatus[i].stepKey,
            stepLabel: crfStepStatus[i].stepLabel,
            stepStatus: crfStepStatus[i].status,
            verificationRequired: crfStepStatus[i].verificationRequired,
            userAccess: crfStepStatus[i].userAccess,
            order: crfStepStatus[i].order,
            col1: crfStepStatus[i].stepLabel,
            crfMasterId: crfStepStatus[i].crfMasterId,
          };
          crfStepStatus[i].sectionStatuses.forEach((section) => {
            if (!columns.some((col) => col.key === section.sectionKey)) {
              const sectionData = _.find(stepData?.sections, (_section) => _section.key === section.sectionKey);
              columns.push({
                key: section.sectionKey,
                parentKey: crfStepStatus[i].stepKey,
                label: sectionData?.lableAbrivation ? sectionData.lableAbrivation : section.sectionLabel,
                lableAbrivation: sectionData?.lableAbrivation ? section.sectionLabel : '',
                maxWidth: 100,
                align: 'center',
              });
            }
            if (section.status === 'Pending' && section.subSectionStatuses.length !== 0) {
              let incomplete = section?.subSectionStatuses.filter((subSection) => subSection.status === 'Pending');
              row[section.sectionKey] = `${section?.subSectionStatuses.length - incomplete.length}/${
                section?.subSectionStatuses.length
              }`;
            } else {
              row[section.sectionKey] = section?.status;
            }
          });
          rows.push(row);
        }
        return { columns, rows };
      }
    }
  }, [crfStepStatus, view, crfFormData]);

  const handleVisitNavigate = (visitKey, crfMasterId, status) => {
    const userRole = getUserRole(loggedInUser, trialId, siteId, userData);

    if (subjectDetailWithSteps?.status === SUBJECT_STATUSES.ON_HOLD) {
      dispatch(
        openMessage({
          message: 'This subject is On Hold due to balancing criteria exceeded, Can not fill the forms.',
          messageSeverity: 'warning',
        })
      );
      return;
    }

    let enrollment = enrollmentStepStatus.find((step) => {
      return step.status === 'Pending';
    });

    const _crfStepStatus = _.find(crfStepStatus, (step) => step.stepKey === visitKey);
    const isNotPending = (section) => {
      return (
        section.status !== 'Pending' ||
        (!_.isEmpty(section.subSectionStatuses) &&
          _.some(section.subSectionStatuses, (subSection) => subSection.status !== 'Pending'))
      );
    };
    const isAnyNotPending = _.some(_crfStepStatus.sectionStatuses, isNotPending);

    if (userRole === 'site_monitor' || userRole === 'data_manager') {
      if (['External_Verification_Pending', 'Completed', 'Verification_Pending', "Pending"].includes(status)) {
        navigate(
          `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/crf/${crfMasterId}/${visitKey}`
        );
      } else if (['Withdrawal', 'Enrollment_Failure', 'Screening_Failure'].includes(status)) {
        const message =
          status === 'Withdrawal'
            ? 'This is a withdrawn subject, Can not view the forms.'
            : status === 'Enrollment_Failure'
            ? 'This is a eligibility failed subject, Can not view the forms.'
            : status === 'Screening_Failure'
            ? 'This is a screening failed subject, Can not view the forms.'
            : 'Visit is pending.';
        if (isAnyNotPending) {
          navigate(
            `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/crf/${crfMasterId}/${visitKey}`,
            {}
          );
          dispatch(
            openMessage({
              message,
              messageSeverity: 'warning',
            })
          );
        } else {
          dispatch(
            openMessage({
              message,
              messageSeverity: 'warning',
            })
          );
        }
      } else {
        if (enrollment && enrollment?.length !== 0) {
          return dispatch(
            openMessage({
              message: 'Subject enrollment process is pending, Can not view the forms.',
              messageSeverity: 'warning',
            })
          );
        } else {
          // navigate(
          //   `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/crf/${crfMasterId}/${visitKey}`
          // );
          dispatch(
            openMessage({
              message: 'Forms are pending for this subject.',
              messageSeverity: 'warning',
            })
          );
        }
      }
      return;
    } else if (enrollment && enrollment?.length !== 0) {
      return dispatch(
        openMessage({
          message: 'Subject enrollment process is pending, Can not view the forms.',
          messageSeverity: 'warning',
        })
      );
    } else if (status === 'Pending') {
      let currentStep = crfStepStatus.find((step) => step.stepKey === visitKey);
      let orderedSteps = _.sortBy(crfStepStatus, 'order');
      let previousStepStatus = false;
      _.forEach(orderedSteps, (step) => {
        if (currentStep.order > step.order && step.status === 'Pending') previousStepStatus = true;
      });
      if (previousStepStatus) {
        dispatch(
          openMessage({
            message: 'Previous visit is pending, Can not view the forms.',
            messageSeverity: 'warning',
          })
        );
      } else {
        navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/${visitKey}/${subjectMasterId}/crf/${crfMasterId}`);
      }
    } else if (['Completed', 'External_Verification_Pending'].includes(status)) {
      navigate(
        `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/crf/${crfMasterId}/${visitKey}`,
        {}
      );
    } else if (['Withdrawal', 'Enrollment_Failure', 'Screening_Failure'].includes(status)) {
      const message =
        status === 'Withdrawal'
          ? 'This is a withdrawn subject, Can not fill the forms.'
          : status === 'Enrollment_Failure'
          ? 'This is a eligibility failed subject, Can not fill the forms.'
          : status === 'Screening_Failure'
          ? 'This is a screening failed subject, Can not fill the forms.'
          : 'Visit is pending, Can not fill the forms.';
      if (isAnyNotPending) {
        navigate(
          `/eConcent/${trialId}/trial-site/${trialSiteId}/site/${siteId}/subject/${subjectMasterId}/crf/${crfMasterId}/${visitKey}`,
          {}
        );
        dispatch(
          openMessage({
            message,
            messageSeverity: 'warning',
          })
        );
      } else {
        dispatch(
          openMessage({
            message,
            messageSeverity: 'warning',
          })
        );
      }
    } else {
      navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/${visitKey}/${subjectMasterId}/crf/${crfMasterId}`);
    }
  };

  const handleWithdrawNavigation = () => {
    navigate(`/eConcent/${trialId}/trial-site/${trialSiteId}/Subject_Withdrawal_Form/${subjectMasterId}/withdraw`);
  };
  return (
    <Grid container rowGap={2}>
      <Grid item xs={12}>
        <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pb: 0 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: 1,
              flexShrink: 0,
            }}
          >
            <RightArrow leftArrow />
            <Typography variant="subtitle1" sx={{ textTransform: 'none', color: theme.palette.primary.main }}>
              Back
            </Typography>
          </Box>
          <SubjectParticipantIdHeading />
          <Box sx={{ display: 'flex', flexShrink: 0 }}>
            {/* {(userRole === 'PI' || userRole === 'site_coordinator') &&
              !_.find(enrollmentStepStatus, (step) => step.status === 'Pending') &&
              _.find(crfStepStatus, (step) => step.status === 'Pending') && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CustomButton variant="outlined" style={{ marginRight: 15 }} onClick={handleWithdrawNavigation}>
                    Withdraw
                  </CustomButton>
                </Box>
                )} */}
            { (['site_monitor', 'data_manager', 'PI', 'site_coordinator'].includes(userRole)) && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <CustomButton variant={'contained'} size="small" onClick={() => setQueryDialogOpen(true)}>
                  <Typography variant="subtitle2" color="inherit" fontWeight={600}>
                    Queries
                  </Typography>
                </CustomButton>
              </Box>
            )}
            {(['site_monitor', 'data_manager'].includes(userRole)) && (
              <AuditExportButton
                subjectNumber={subjectDetailWithSteps?.subjectNumber || subjectDetailWithSteps?.participantId}
                protocolNumber={trialDetails?.trialData?.protocolNumber}
                style={{ marginRight: 15 }}
              />
            )}
            {_.map(buttonProperty?.actionButtons, (buttonData) => {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title={buttonData?.lableAbrivation} arrow placement="top">
                    <CustomButton
                      key={buttonData.buttonLabel}
                      variant="contained"
                      onClick={buttonData?.actionClick}
                      style={{ marginRight: 15 }}
                    >
                      <Typography variant="subtitle2" sx={{ textTransform: 'none' }} fontWeight={600}>
                        {buttonData.buttonLabel}
                      </Typography>
                    </CustomButton>
                  </Tooltip>
                </Box>
              );
            })}
            {buttonProperty?.buttonLabel && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={buttonProperty?.buttonLableAbrivation} arrow placement="top">
                  <CustomButton
                    variant="contained"
                    onClick={buttonProperty?.actionClick}
                    disabled={buttonProperty?.disabled}
                  >
                    <Typography variant="subtitle2" sx={{ textTransform: 'none' }} fontWeight={600}>
                      {buttonProperty?.buttonLabel}
                    </Typography>
                  </CustomButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <SubjectDetailCRFInfo
          // subjectStepStatusData={enrollmentStepStatus}
          stepStatusData={enrollmentStepStatus}
          visitStatusData={buttonProperty?.visitStatusData}
          subjectName={subjectDetailWithSteps && subjectDetailWithSteps?.participantId}
          subjectDetailWithSteps={subjectDetailWithSteps}
        />
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ marginTop: 5 }}>
          <VisitList
            columns={tableData?.columns}
            rows={tableData?.rows}
            view={view}
            setView={setView}
            handleVisitNavigate={handleVisitNavigate}
          />
        </Box>
      </Grid>
      <QueryListDialog
        open={queryDialogOpen}
        onClose={() => setQueryDialogOpen(false)}
        sponsorId={sponsorId}
        trialId={trialId}
        siteId={siteId}
        trialSiteId={trialSiteId}
        stepKey={queryParams.stepKey}
        sectionKey={queryParams.sectionKey}
        subSectionKey={queryParams.subSectionKey}
        subjectMasterId={subjectMasterId}
      />
      {/* Re-Enroll Confirmation Dialog */}
      <ConfirmationDialog
        open={reEnrollDialogOpen}
        handleClose={closeReEnrollDialog}
        handleConfirm={handleReEnrollConfirm}
        title="Re-Enroll Subject"
        message={`Are you sure you want to re-enroll subject "${
          subjectDetailWithSteps?.participantId || ''
        }" back to the study?\n\nThis will move the subject from the Holding Area and resume their enrollment process.`}
        buttonLabel={isLoading ? 'Re-Enrolling...' : 'Re-Enroll'}
        cancelButtonLabel="Cancel"
      />
    </Grid>
  );
};

export default SubjectDetails;
